import { useEffect, useState } from "react"
import { PSEUDO_FULLSCREEN_EVENT, pseudoFullscreenElement } from "./useFullscreen"

/**
 * Where meeting overlays have to be rendered for the user to actually see them.
 *
 * A fullscreened element is the ONLY thing the browser paints — everything else
 * in the document is still there, still updating, simply not on screen. So when
 * a shared screen, a shared video, a document or a maximised tile owns the
 * fullscreen layer, every panel and dialog anchored at `document.body` is drawn
 * somewhere invisible. The shortcut fired, the state changed, and nothing
 * appeared: the keyboard looks broken when it is not.
 *
 * Whole-page fullscreen is deliberately NOT redirected. `document.documentElement`
 * contains the body already, so overlays are painted correctly where they are,
 * and moving them would mean portalling a div straight into `<html>` for no gain.
 * That is what `contains(document.body)` tests for — it covers `documentElement`
 * and any wrapper high enough to hold the whole app.
 *
 * Identical on Windows, Linux and macOS: `fullscreenElement` is standard, and
 * the webkit-prefixed pair is read for older Safari. Note that a browser put
 * into fullscreen by F11 (Windows/Linux) or the green button (macOS) is NOT
 * using the Fullscreen API at all — nothing is `fullscreenElement`, the page is
 * laid out normally, and this correctly returns `document.body`.
 */

interface VendorDocument extends Document {
  webkitFullscreenElement?: Element | null
}

/**
 * The element currently owning the fullscreen layer, or null. An element pinned
 * in-page (iPhone, see setPseudoFullscreen) counts: it sits above everything at
 * the top z-index, so overlays left at `document.body` would open underneath it.
 */
export function activeFullscreenElement(): Element | null {
  const doc = document as VendorDocument
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? pseudoFullscreenElement()
}

/**
 * The host to portal into, resolved once. Returns `document.body` whenever
 * nothing is fullscreen or the fullscreen element already contains the body.
 */
export function resolveFullscreenHost(): HTMLElement {
  const fs = activeFullscreenElement()
  if (!fs || !(fs instanceof HTMLElement)) return document.body
  if (fs === document.documentElement || fs.contains(document.body)) return document.body
  return fs
}

/**
 * Live version of the above: re-resolves on every fullscreen transition,
 * including the user pressing Esc.
 */
export function useFullscreenHost(): HTMLElement | null {
  // Null on the first render under SSR; every consumer already guards for it.
  const [host, setHost] = useState<HTMLElement | null>(() =>
    typeof document === "undefined" ? null : resolveFullscreenHost(),
  )

  useEffect(() => {
    const sync = () => setHost(resolveFullscreenHost())
    sync()
    document.addEventListener("fullscreenchange", sync)
    document.addEventListener("webkitfullscreenchange", sync)
    document.addEventListener(PSEUDO_FULLSCREEN_EVENT, sync)
    return () => {
      document.removeEventListener("fullscreenchange", sync)
      document.removeEventListener("webkitfullscreenchange", sync)
      document.removeEventListener(PSEUDO_FULLSCREEN_EVENT, sync)
    }
  }, [])

  return host
}

/**
 * True while a NESTED element owns the fullscreen layer — the case where the
 * meeting's own chrome is off screen and overlays have to be re-homed.
 */
export function useNestedFullscreen(): boolean {
  const host = useFullscreenHost()
  return host !== null && host !== document.body
}
